import { message } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { USER_ACCESS_TOKEN, USER_KEY } from 'src/constants';
import { ENDPOINT, HTTP_STATUS_RESPONSE_KEY } from 'src/constants/api';
import { DTO, ResponseDTO } from 'src/dto/base.dto';
import { API_HOST } from 'src/environments/environment';
import i18n, { i18nKey } from 'src/locales/i18n';
import eventEmitter from 'src/store/event';
import qs from 'qs';
import { LIST_PATH_NOT_SHOW_ERROR_MESSAGE } from 'src/constants/route';

export interface IHttpService {
	setToken(token?: string): void;
	request<reqT extends DTO, resT>(dto: reqT): Promise<ResponseDTO<resT>>;
	uploadFile<resT>(url: string, form: FormData): Promise<ResponseDTO<resT>>;
	downloadFile(url: string, headers: object | undefined, meta: { name: string }): Promise<void>;
}

type I18nKeyType = keyof typeof i18nKey.httpResponse;
const cleanUpStorage = () => {
	localStorage.removeItem(USER_ACCESS_TOKEN);
	localStorage.removeItem(USER_KEY);
};
const onResponse = (response: AxiosResponse): AxiosResponse => {
	const isMatchError = LIST_PATH_NOT_SHOW_ERROR_MESSAGE.some((x) => x === location.pathname);
	const convertedResponse: ResponseDTO<unknown> = response.data;
	if (convertedResponse.responseKey && convertedResponse.responseKey !== HTTP_STATUS_RESPONSE_KEY.SUCCESS) {
		if (convertedResponse.responseKey === HTTP_STATUS_RESPONSE_KEY.USER_DONT_HAVE_PERMISSION) {
			if (!isMatchError) {
				message.error(i18n.t(`${i18nKey.httpResponse.incorrectLoginCredential}`));
			}
		} else if (convertedResponse.responseKey === HTTP_STATUS_RESPONSE_KEY.INVITATION_LINK_EXPIRED) {
			message.error(i18n.t(`${i18nKey.httpResponse.invitationLinkExpired}`));
		} else if (
			convertedResponse.responseKey === HTTP_STATUS_RESPONSE_KEY.GUARANTOR_COULD_NOT_BECOME_THEIR_OWN_GUARANTOR
		) {
			message.error(i18n.t(`${i18nKey.httpResponse.guaranteeCouldNotBecomeTheirOwnGuarantor}`));
		} else if (convertedResponse.responseKey === HTTP_STATUS_RESPONSE_KEY.USER_IS_INACTIVE) {
			message.error(i18n.t(`${i18nKey.httpResponse.userIsInactive}`));
			eventEmitter.emit('logout');
			setTimeout(() => {
				cleanUpStorage();
				location.assign('/login');
			}, 350);
		}
		else {
			const msg = (convertedResponse.responseKey || 'unknown') as I18nKeyType;
			message.error(i18n.t(`${i18nKey.httpResponse[msg]}`));
		}
		return response;
	}

	if (response.status == 400) {
		response.data = {
			responseKey: 'badRequest'
		};
		message.error(i18n.t(`${i18nKey.httpResponse.badRequest}`));
	} else if (response.status == 401) {
		message.error(i18n.t(`${i18nKey.httpResponse.tokenInvalid}`));
		eventEmitter.emit('logout');
		setTimeout(() => {
			cleanUpStorage();
			location.assign('/');
		}, 350);
	}
	return response;
};
const onResponseError = (error: AxiosError): Promise<AxiosError> => {
	message.error(i18n.t(`${i18nKey.httpResponse.unknown}`));
	return Promise.reject(error);
};

export class HttpService implements IHttpService {
	private token: string | null;
	constructor() {
		this.token = localStorage.getItem(USER_ACCESS_TOKEN);
		axios.defaults.baseURL = API_HOST + ENDPOINT.BASE_URL;
		axios.defaults.validateStatus = (status: number) => status < 500;
		axios.interceptors.response.use(onResponse, onResponseError);
	}

	public async request<reqT extends DTO, resT>(dto: reqT): Promise<ResponseDTO<resT>> {
		try {
			const response = await axios({
				method: dto.method,
				headers: {
					Authorization: this.token ? 'Bearer ' + this.token : '',
					'X-Frame-Options': 'DENY',
					'Content-Security-Policy': "frame-ancestors 'self';",
					...dto.headers
				},
				url: this.replaceURLToParamURL(dto.url, dto.param || {}),
				data: dto.body,
				params: dto.query,
				responseType: dto.responseType,
				paramsSerializer: {
					serialize: (params) => {
						return qs.stringify(params, {
							arrayFormat: 'repeat',
							encode: false,
							allowDots: true
						});
					}
				}
			});
			const data = response.data || response.data.data;
			return { ...data };
		} catch (error: unknown) {
			return {
				data: undefined,
				responseKey: 'unknown'
			};
		}
	}

	public setToken(token: string): void {
		this.token = token;
		localStorage.setItem(USER_ACCESS_TOKEN, token);
	}

	public async uploadFile<resT>(url: string, form: FormData): Promise<ResponseDTO<resT>> {
		try {
			const response = await axios({
				method: 'POST',
				headers: {
					Authorization: this.token ? 'Bearer ' + this.token : '',
					'X-Frame-Options': 'DENY',
					'Content-Security-Policy': "frame-ancestors 'self';",
					'Content-Type': 'multipart/form-data'
				},
				url: url,
				responseType: 'json',
				data: form
			});
			const data = response.data || response.data.data;
			return { ...data };
		} catch (error: unknown) {
			return {
				data: undefined,
				responseKey: 'unknown'
			};
		}
	}
	public async downloadFile(url: string, headers: object | undefined, meta: { name: string }): Promise<void> {
		await axios({
			url: url,
			headers: {
				Authorization: this.token ? 'Bearer ' + this.token : '',
				...headers
			},
			method: 'GET',
			responseType: 'blob'
		}).then((response) => {
			const href = URL.createObjectURL(response.data);

			const link = document.createElement('a');
			link.href = href;
			link.setAttribute('download', meta.name);
			document.body.appendChild(link);
			link.click();

			document.body.removeChild(link);
			URL.revokeObjectURL(href);
		});
	}

	private replaceURLToParamURL = (url: string, param: object): string => {
		let newUrl = url;
		Object.entries(param).forEach(([key, value]) => {
			newUrl = newUrl.replace(':' + key.toString(), value.toString());
		});
		return newUrl;
	};
}

const httpClient = new HttpService();
export default httpClient;
